import { NameCell, PhoneCell, TotalsLabel } from "@";
import { formatNumber } from "@/utils/formatting.js";
import type { ColumnDef } from "@tanstack/react-table";
import type { AssignmentStatistic } from "../types.ts";
import { DataTable } from "@/shared/components/tanstackTable";
import { useAssignmentStatisticsQuery } from "../api/queries";
import RefetchDataOnError from "./RefetchData.tsx";

const ASSIGNMENT_TOTAL_ROW_ID = "assignment-total-row";

const isTotalRow = (id?: string) => id === ASSIGNMENT_TOTAL_ROW_ID;

// Driver name — totals row gets the shared TotalsLabel pill, regular rows
// get the shared NameCell.
const DriverNameCell = ({
  row,
}: {
  row: { original: AssignmentStatistic };
}) =>
  isTotalRow(row.original.driverId) ? (
    <TotalsLabel />
  ) : (
    <NameCell name={row.original.driverName} />
  );

// NumberCell stays local — it has the `isTotal` branch (bold + tone) which
// the shared AmountCell doesn't model. Two consumers share this pattern
// (Assignment + Quality stats) but it's stats-table-specific.
const NumberCell = ({
  value,
  isTotal,
  tone = "default",
}: {
  value: number;
  isTotal: boolean;
  tone?: "default" | "emerald" | "rose";
}) => {
  const totalClass =
    tone === "emerald"
      ? "text-emerald-700 dark:text-emerald-300"
      : tone === "rose"
        ? "text-rose-700 dark:text-rose-300"
        : "text-purple-900 dark:text-purple-300";
  return (
    <span
      className={`tabular-nums ${
        isTotal
          ? `font-bold ${totalClass}`
          : "font-medium text-gray-700 dark:text-gray-300"
      }`}
    >
      {formatNumber(value)}
    </span>
  );
};

const assignmentTableColumns: ColumnDef<AssignmentStatistic>[] = [
  {
    id: "driverName",
    header: "اسم المندوب",
    size: 180,
    cell: ({ row }) => (
      <DriverNameCell row={row as { original: AssignmentStatistic }} />
    ),
  },
  {
    id: "phoneNumber",
    header: "رقم الجوال",
    size: 140,
    cell: ({ row }) => <PhoneCell phone={row.original.phoneNumber} />,
  },
  {
    id: "totalVerification",
    header: "إجمالي التوثيقات",
    size: 130,
    cell: ({ row }) => (
      <NumberCell
        value={row.original.totalVerification}
        isTotal={isTotalRow(row.original.driverId)}
      />
    ),
  },
  {
    id: "completedVerification",
    header: "التوثيقات المكتملة",
    size: 130,
    cell: ({ row }) => (
      <NumberCell
        value={row.original.completedVerification}
        isTotal={isTotalRow(row.original.driverId)}
        tone="emerald"
      />
    ),
  },
  {
    id: "notCompletedVerification",
    header: "التوثيقات غير المكتملة",
    size: 130,
    cell: ({ row }) => (
      <NumberCell
        value={row.original.notCompletedVerification}
        isTotal={isTotalRow(row.original.driverId)}
        tone="rose"
      />
    ),
  },
];

const AssignmentStatistics = ({
  fromDate,
  toDate,
}: {
  fromDate: string;
  toDate: string;
}) => {
  const {
    data: assignmentData,
    isLoading: isLoadingAssignment,
    isError,
    refetch,
  } = useAssignmentStatisticsQuery(fromDate, toDate);

  if (isError) {
    return <RefetchDataOnError refetch={refetch} />;
  }

  const assignmentDataWithTotals =
    assignmentData && assignmentData.length > 0
      ? [
          ...assignmentData,
          {
            driverId: ASSIGNMENT_TOTAL_ROW_ID,
            driverName: "الإجمالي",
            phoneNumber: "",
            totalVerification: assignmentData.reduce(
              (total, item) => total + item.totalVerification,
              0,
            ),
            completedVerification: assignmentData.reduce(
              (total, item) => total + item.completedVerification,
              0,
            ),
            notCompletedVerification: assignmentData.reduce(
              (total, item) => total + item.notCompletedVerification,
              0,
            ),
          },
        ]
      : assignmentData;

  return (
    <DataTable
      className="card relative overflow-hidden"
      data={assignmentDataWithTotals}
      columns={assignmentTableColumns}
      isLoading={isLoadingAssignment}
    />
  );
};

export default AssignmentStatistics;
